<template>
  <div class="scope-filter">
    <m-spinner
      v-if="loading"
      size="sm"
    />
    <template v-else>
      <div
        v-for="(child, index) in userScopeTree.children"
        :key="getKey(child)"
        class="_filter-item"
      >
        <div
          v-if="index === 0 && allowGrouping"
          class="_group-label"
        >
          <m-focusable
            hide-border
            hide-hover
          >
            {{ $t('userScopeFilter.where') }}
          </m-focusable>
        </div>
        <div
          v-if="child.isGroup && index !== 0"
          class="_group-label"
        >
          <m-select
            v-model:value="selectedOperator"
            :items="opItems"
            :read-only="index !== 1"
            :hide-border="index !== 1"
            :placeholder="$t('leafNode.placeholder')"
          />
        </div>
        <div
          v-if="child.isGroup"
          class="_group"
        >
          <scope-filter
            :value="child"
            :props="props"
            :account="account"
            :max-tag-text-length="15"
            :has-dynamic-dates="hasDynamicDates"
            :disabled="disabled"
            :multi-model="multiModel"
            :open-with-prop="openWithProp"
            :depth="depth + 1"
            :allow-grouping="allowGrouping"
            :max-tags="maxTags"
            show-empty-info
            can-remove-all
            skip-validation
            @input="updateGroupFilter"
          />
        </div>
        <div
          v-if="child.isGroup && !disabled"
          class="_group-menu"
        >
          <m-dropdown
            :title="$t('general.actions')"
            close-on-click
          >
            <m-btn
              icon="ellipsis"
              hide-border
              fab
              light
            />
            <template #overlay>
              <m-card
                no-padding
                list
              >
                <m-card-item
                  v-if="userScopeTree.children.length > 1 || canRemoveAll"
                  data-cy="delete-filter"
                  icon="delete"
                  @click="remove(index)"
                >
                  {{ $t('userScopeFilter.remove') }}
                </m-card-item>
                <m-card-item
                  icon="copy"
                  @click="duplicate(index)"
                >
                  {{ $t('general.duplicate') }}
                </m-card-item>
                <m-card-item
                  v-if="child.children != null && child.children.length === 1"
                  icon="wrap"
                  @click="ungroup(index)"
                >
                  <template v-if="calculateDepth(child) > 2">
                    {{ $t('userScopeFilter.resolveGroup') }}
                  </template>
                  <template v-else>
                    {{ $t('userScopeFilter.turnIntoFilter') }}
                  </template>
                </m-card-item>
                <m-card-item
                  v-if="calculateDepth(child) + depth < 4"
                  icon="wrap"
                  @click="group(index)"
                >
                  <div>
                    <div class="_wrap-in-group-title">
                      {{ $t('userScopeFilter.wrapInGroup') }}
                    </div>
                    <div class="_wrap-in-group-sub-title">
                      {{ $t('userScopeFilter.wrapInGroupSubTitle') }}
                    </div>
                  </div>
                </m-card-item>
              </m-card>
            </template>
          </m-dropdown>
        </div>
        <leaf-node
          v-if="!child.isGroup"
          :class="['_leaf', disabled ? '-disabled':'']"
          :node="child"
          :op="selectedOperator"
          :hide-op="hideOp || index === 0"
          :can-select-op="index === 1"
          :hide-leaf-op="hideLeafOp"
          :can-delete="userScopeTree.children.length > 1 || canRemoveAll"
          :scope-items="scopeItems"
          :read-only="disabled"
          :max-tag-text-length="maxTagTextLength"
          :has-dynamic-dates="hasDynamicDates"
          :multi-model="multiModel"
          :allow-grouping="allowGrouping && depth < 3"
          :max-tags="maxTags"
          @change-operator="changeOperator"
          @change="updateChild(index, $event)"
          @delete="remove(index)"
          @duplicate="duplicate(index)"
          @group="group(index)"
        />
      </div>
    </template>
    <div
      v-if="!disabled && firstUnusedScopeItem !== null"
      class="_add"
    >
      <m-btn
        v-if="depth > 1 || !allowGrouping"
        class="_btn"
        data-cy="add-advanced-filter-item"
        icon="plus"
        :disabled="disabled"
        light
        hide-border
        small
        @click="addNode(firstUnusedScopeItem)"
      >
        <template v-if="addBtnLabel === ''">
          {{ $t('userScopeFilter.addFilter') }}
        </template>
        <template v-else>
          {{ addBtnLabel }}
        </template>
      </m-btn>
      <m-dropdown
        v-else
        close-on-click
        :title="$t('general.add')"
      >
        <m-btn
          class="_btn"
          icon="plus"
          :disabled="disabled"
          light
          hide-border
          small
        >
          <template v-if="addBtnLabel === ''">
            {{ $t('userScopeFilter.addFilter') }}
          </template>
          <template v-else>
            {{ addBtnLabel }}
          </template>
          <m-icon
            class="_down-icon"
            type="down"
            :color="$colors.grey.lighten1"
            size="11"
          />
        </m-btn>

        <template #overlay>
          <m-card
            no-padding
            list
          >
            <m-card-item
              data-cy="add-filter"
              icon="plus"
              @click="addNode(firstUnusedScopeItem)"
            >
              {{ $t('userScopeFilter.addFilter') }}
            </m-card-item>
            <m-card-item
              icon="add-group"
              @click="addGroupNode(firstUnusedScopeItem, userScopeTree.key)"
            >
              {{ $t('userScopeFilter.addGroupFilter') }}
            </m-card-item>
          </m-card>
        </template>
      </m-dropdown>
    </div>
    <div
      v-if="showEmptyInfo && disabled && userScopeTree.children.length === 0"
      class="_empty"
    >
      {{ $t('userScopeFilter.empty') }}
    </div>
  </div>
</template>

<script>
import LeafNode from '@/components/filter/LeafNode.vue';
import useScopeFilter from '@/composables/filter/scope-filter';
import useScopeItems from '@/composables/filter/scope-items';
import { copy } from 'shared/lib/copy';
import { guid } from 'shared/lib/uuid';
import { isEqual } from 'lodash-es';
import { isValidTree } from '@/lib/filter/scope-tree';
import { ref, toRef } from 'vue';
import { userScopeOperator } from 'shared/constants.json';

export default {
  name: 'ScopeFilter',
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    props: {
      type: Array,
      default: () => [],
    },
    account: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showEmptyInfo: {
      type: Boolean,
      default: false,
    },
    showStaticUserSelection: {
      type: Boolean,
      default: false,
    },
    openWithStaticUserSelection: {
      type: Boolean,
      default: false,
    },
    canRemoveAll: {
      type: Boolean,
      default: false,
    },
    maxTagTextLength: {
      type: Number,
      default: 0,
    },
    hasDynamicDates: {
      type: Boolean,
      default: false,
    },
    multiModel: {
      type: Boolean,
      default: false,
    },
    initWithProp: {
      type: Object,
      default: () => null,
    },
    openWithProp: {
      type: Object,
      default: () => null,
    },
    hideOp: {
      type: Boolean,
      default: false,
    },
    hideLeafOp: {
      type: Boolean,
      default: false,
    },
    addBtnLabel: {
      type: String,
      default: '',
    },
    depth: {
      type: Number,
      default: 0,
    },
    allowGrouping: {
      type: Boolean,
      default: false,
    },
    skipValidation: {
      type: Boolean,
      default: false,
    },
    maxTags: {
      type: Number,
      default: 2,
    },
  },
  setup(props) {
    const {
      staticUserItem,
      scopeItems,
      scopeItemByProp,
    } = useScopeItems(toRef(props, 'props'), props.showStaticUserSelection, props.multiModel);

    const userScopeTree = ref(null);
    const scopeFilterSvc = useScopeFilter(scopeItems, userScopeTree, ref([]));
    return {
      staticUserItem,
      scopeItems,
      scopeItemByProp,
      userScopeTree,
      scopeFilterSvc,
      usedScopeItems: scopeFilterSvc.usedScopeItems,
      unusedScopeItems: scopeFilterSvc.unusedScopeItems,
    };
  },
  emits: ['input', 'update:value'],
  components: { LeafNode },
  data() {
    return {
      selectedOperator: userScopeOperator.and,
      opItems: [
        {
          text: this.$t('general.and'),
          value: userScopeOperator.and,
        },
        {
          text: this.$t('general.or'),
          value: userScopeOperator.or,
        },
      ],
    };
  },
  computed: {
    firstUnusedScopeItem() {
      if (this.unusedScopeItems.length === 0) {
        return null;
      }
      return this.unusedScopeItems[0];
    },
  },
  methods: {
    calculateDepth(node) {
      if (node.children === undefined || node.children.length === 0) {
        return 0;
      }
      let maxDepth = 0;
      for (let i = 0; i < node.children.length; i++) {
        const depth = this.calculateDepth(node.children[i]);
        if (depth > maxDepth) {
          maxDepth = depth;
        }
      }
      return 1 + maxDepth;
    },
    changeOperator(val) {
      this.selectedOperator = val;
      this.emitUpdate({
        ...this.userScopeTree,
        op: val,
      });
    },
    updateChild(index, val) {
      this.userScopeTree.children.splice(index, 1, val);
    },
    init() {
      this.userScopeTree = {
        account: { uid: this.account.uid },
        op: userScopeOperator.and,
        children: [],
      };
      if (this.initWithProp !== null) {
        const prop = this.allowedProps.find((p) => p.key === this.initWithProp.key);
        this.addNode(prop);
      }
    },
    getKey(node) {
      if (typeof node.uid !== 'undefined') {
        return node.uid;
      }
      if (typeof node.key !== 'undefined') {
        return node.key;
      }
      return 0;
    },
    isUsedScopeItem(scopeItem) {
      return this.usedScopeItems.find((p) => p.value === scopeItem.value) !== undefined;
    },
    addNode(scopeItem = null) {
      if (scopeItem === null || this.isUsedScopeItem(scopeItem)) {
        return;
      }
      if (!Array.isArray(this.userScopeTree.children)) {
        this.userScopeTree.children = [];
      }
      this.userScopeTree.children.push(this.scopeFilterSvc.buildNode(scopeItem));
    },
    addGroupNode(scopeItem) {
      if (!Array.isArray(this.userScopeTree.children)) {
        this.userScopeTree.children = [];
      }
      this.userScopeTree.children.push(this.scopeFilterSvc.buildGroup(this.scopeFilterSvc.buildNode(scopeItem)));
    },
    updateGroupFilter(value) {
      for (let i = 0; i < this.userScopeTree.children.length; i++) {
        if (this.userScopeTree.children[i].key === value.key) {
          this.userScopeTree.children[i] = value;
        }
      }
    },
    remove(index) {
      this.userScopeTree.children.splice(index, 1);
    },
    recreateKey(node) {
      node.key = guid();
      if (node.children === undefined || node.children.length === 0) {
        return node;
      }
      for (let i = 0; i < node.children.length; i++) {
        this.recreateKey(node.children[i]);
      }
      return node;
    },
    duplicate(index) {
      const node = this.recreateKey(copy(this.userScopeTree.children[index]));
      this.userScopeTree.children.splice(index + 1, 0, node);
    },
    group(index) {
      const node = this.recreateKey(copy(this.userScopeTree.children[index]));
      this.userScopeTree.children.splice(index, 1, this.scopeFilterSvc.buildGroup(node));
    },
    ungroup(index) {
      const node = this.recreateKey(copy(this.userScopeTree.children[index]));
      this.userScopeTree.children.splice(index, 1, ...node.children);
    },
    emitUpdate(userScopeTree, key) {
      if (!this.skipValidation && !isValidTree(userScopeTree)) {
        return;
      }
      this.$emit('input', copy(userScopeTree), key);
      this.$emit('update:value', copy(userScopeTree));
    },
  },
  watch: {
    userScopeTree: {
      handler(val) {
        if (isEqual(val, this.value)) {
          return;
        }
        if (this.userScopeTree.children.length === 0 && this.depth === 0) {
          this.emitUpdate(null);
          return;
        }
        this.emitUpdate(val);
      },
      deep: true,
    },
    value: {
      handler(val) {
        if (JSON.stringify(this.userScopeTree) === JSON.stringify(val)) {
          return;
        }
        if (this.value === null || Object.keys(this.value).length === 0) {
          this.init();
          return;
        }
        this.userScopeTree = copy(val);
      },
      deep: true,
    },
    selectedOperator: {
      handler(val) {
        this.emitUpdate({
          ...this.userScopeTree,
          op: val,
        });
      },
    },
  },
  created() {
    if (this.value === null || Object.keys(this.value).length === 0) {
      this.init();
      this.emitUpdate(this.userScopeTree);
      return;
    }
    this.userScopeTree = copy(this.value);
    this.selectedOperator = this.userScopeTree.op;

    if (this.showStaticUserSelection === true && this.openWithStaticUserSelection === true) {
      this.addNode(this.staticUserItem);
    }
    if (this.openWithProp !== null) {
      // TODO: Maybe there's a bug here with multi-modal
      const filterToAdd = this.allowedProps.find((p) => p.key === this.openWithProp.key);
      if (filterToAdd !== undefined) {
        this.addNode(this.scopeItemByProp(filterToAdd));
      }
    }
  },
};
</script>

<style lang="scss" type="text/scss">
.scope-filter {
  display: flex;
  flex-direction: column;
  gap: .8rem;

  ._add {
    min-width: 30rem;

    ._btn {
      ._down-icon {
        padding-left: .4rem;
      }
    }
  }

  ._filter-item {
    display: flex;

    ._group-label {
      padding-right: 0.8rem;
      width: 7rem;
      display: flex;
      align-items: flex-start;
      justify-content: flex-end;
    }

    ._group-menu {
      padding-left: 0.8rem;
    }

    ._group {
      display: flex;
      padding: 0.8rem;
      border: 1px solid $border-color;
      border-radius: $input-field-border-radius;
      background-color: map_get($grey, 'lighten-7');
    }

    ._inner {
      width: 100%;
    }
  }

  ._empty {
    color: $font-color-tertiary;
  }
}

._wrap-in-group-title {
  margin-bottom: 0.4rem;
}

._wrap-in-group-sub-title {
  font-size: 1.2rem;
  color: $font-color-secondary;
}
</style>
